import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import './DinoLoader.less';

export const W = 600;
export const H = 150;
export const GROUND = 128;
export const JUMP_VY = -11;

const FG = '#535353';
const BG = '#f7f7f7';

export const hit = (a, b) => (
  a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y
);

const cactusBox = (o) => ({
  x: o.x,
  y: GROUND - o.h,
  w: o.w,
  h: o.h,
});

export const dinoBox = (g) => (
  g.duck
    ? {
      x: 50, y: g.y - 18, w: 46, h: 18,
    }
    : {
      x: 56, y: g.y - 46, w: 34, h: 42,
    }
);

export const createGame = ({ random = Math.random } = {}) => {
  const g = {
    status: 'wait',
    t: 0,
    score: 0,
    speed: 6,
    y: GROUND,
    vy: 0,
    duck: false,
    frame: 0,
    obstacles: [],
    clouds: [
      { x: 140, y: 28 },
      { x: 360, y: 18 },
      { x: 540, y: 34 },
    ],
  };

  const reset = () => {
    g.status = 'run';
    g.t = 0;
    g.score = 0;
    g.speed = 6;
    g.y = GROUND;
    g.vy = 0;
    g.duck = false;
    g.obstacles = [];
  };

  const jump = () => {
    if (g.status === 'dead') {
      reset();
      return true;
    }
    if (g.status === 'wait') g.status = 'run';
    if (g.y < GROUND || g.vy !== 0) return false;
    g.vy = JUMP_VY;
    g.duck = false;
    return true;
  };

  const setDuck = (on) => {
    if (g.status === 'dead') return;
    if (on && g.status === 'wait') g.status = 'run';
    g.duck = Boolean(on) && g.y >= GROUND && g.vy === 0;
  };

  const plant = (x, h = 36) => {
    g.obstacles.push({
      x, w: 14, h, arms: 1,
    });
  };

  const tick = (dt) => {
    const clamped = Math.min(Math.max(dt, 0), 32);
    const step = clamped / 16.67;
    g.t += clamped;
    if (g.status === 'wait') {
      if (g.t >= 500) g.status = 'run';
      return;
    }
    if (g.status !== 'run') return;

    g.speed = Math.min(13, 6 + g.t * 0.00035);
    g.score = Math.floor(g.t * 0.012);
    g.frame += step;
    if (g.y < GROUND || g.vy) {
      g.vy += 0.65 * step;
      g.y += g.vy * step;
      if (g.y >= GROUND) {
        g.y = GROUND;
        g.vy = 0;
      }
    }

    const next = [];
    for (let i = 0; i < g.obstacles.length; i += 1) {
      const o = g.obstacles[i];
      const x = o.x - g.speed * step;
      if (x > -40) next.push({ ...o, x });
    }
    g.obstacles = next;
    const last = g.obstacles[g.obstacles.length - 1];
    const gap = Math.max(400, g.speed * 48) + 80 + random() * 160;
    if (g.t > 400 && (!last || last.x < 620 - gap)) {
      const big = random() > 0.55;
      g.obstacles.push({
        x: 620,
        w: big ? 16 : 12,
        h: big ? 50 : 34,
        arms: big ? 2 : 1,
      });
    }

    for (let i = 0; i < g.clouds.length; i += 1) {
      let { x } = g.clouds[i];
      x -= g.speed * 0.22 * step;
      if (x < -70) x = W + 40;
      g.clouds[i] = { ...g.clouds[i], x };
    }

    const box = dinoBox(g);
    for (let i = 0; i < g.obstacles.length; i += 1) {
      if (hit(box, cactusBox(g.obstacles[i]))) {
        g.status = 'dead';
        break;
      }
    }
  };

  return {
    jump,
    setDuck,
    tick,
    plant,
    dinoBox: () => dinoBox(g),
    get: () => g,
  };
};

const drawDino = (ctx, g) => {
  const x = 52;
  const { y } = g;
  ctx.fillStyle = FG;
  if (g.duck) {
    ctx.fillRect(x, y - 16, 40, 16);
    ctx.fillRect(x + 34, y - 22, 16, 12);
    ctx.fillRect(x + 8 + (Math.floor(g.frame) % 2) * 8, y, 7, 4);
    ctx.fillRect(x + 22, y, 7, 4);
    ctx.fillStyle = BG;
    ctx.fillRect(x + 42, y - 18, 3, 3);
    return;
  }
  const run = g.y >= GROUND && g.status === 'run';
  const a = run && Math.floor(g.frame / 4) % 2 === 0;
  ctx.fillRect(x + 8, y - (a ? 10 : 16), 7, a ? 10 : 16);
  ctx.fillRect(x + 22, y - (a ? 16 : 10), 7, a ? 16 : 10);
  ctx.fillRect(x + 2, y - 28, 12, 10);
  ctx.fillRect(x + 10, y - 34, 22, 20);
  ctx.fillRect(x + 26, y - 48, 20, 18);
  ctx.fillRect(x + 44, y - 42, 6, 8);
  ctx.fillRect(x + 18, y - 22, 8, 3);
  ctx.fillStyle = BG;
  ctx.fillRect(x + 38, y - 44, 3, 3);
  if (g.status === 'dead') {
    ctx.fillStyle = FG;
    ctx.fillRect(x + 37, y - 45, 5, 1);
    ctx.fillRect(x + 39, y - 47, 1, 5);
  }
};

const drawCactus = (ctx, o) => {
  ctx.fillStyle = FG;
  ctx.fillRect(o.x, GROUND - o.h, o.w, o.h);
  ctx.fillRect(o.x - 6, GROUND - o.h + 10, 6, 4);
  ctx.fillRect(o.x - 6, GROUND - o.h + 10, 4, 12);
  if (o.arms > 1) {
    ctx.fillRect(o.x + o.w, GROUND - o.h + 16, 6, 4);
    ctx.fillRect(o.x + o.w + 2, GROUND - o.h + 16, 4, 10);
  }
};

export const drawGame = (ctx, g) => {
  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = '#dadada';
  g.clouds.forEach((c) => {
    ctx.beginPath();
    ctx.ellipse(c.x, c.y, 22, 10, 0, 0, Math.PI * 2);
    ctx.fill();
  });
  ctx.fillStyle = FG;
  ctx.fillRect(0, GROUND, W, 2);
  for (let i = 0; i < 18; i += 1) {
    const px = ((i * 48) - (g.t * 0.35)) % W;
    ctx.fillRect(px < 0 ? px + W : px, GROUND + 4, 2, 1);
  }
  g.obstacles.forEach((o) => drawCactus(ctx, o));
  drawDino(ctx, g);
  ctx.font = '12px monospace';
  ctx.textAlign = 'right';
  ctx.fillText(String(g.score).padStart(5, '0'), W - 10, 18);
  if (g.status === 'wait') {
    ctx.textAlign = 'center';
    ctx.fillText('PRESS SPACE', W / 2, 56);
  }
  if (g.status === 'dead') {
    ctx.textAlign = 'center';
    ctx.fillText('G A M E  O V E R', W / 2, 50);
    ctx.beginPath();
    ctx.arc(W / 2, 72, 8, 0, Math.PI * 2);
    ctx.stroke();
  }
};

const jumpKey = (e) => e.code === 'Space' || e.code === 'ArrowUp' || e.key === ' ';

const DinoLoader = ({ message }) => {
  const wrap = useRef(null);
  const canvas = useRef(null);
  const game = useRef(null);

  useEffect(() => {
    const g = createGame();
    game.current = g;
    const node = canvas.current;
    const box = wrap.current;
    if (box) box.focus();

    const size = () => {
      if (!node || !box) return;
      const cssW = Math.min(800, Math.max(320, box.clientWidth - 32));
      const cssH = Math.round(cssW * (H / W));
      const dpr = window.devicePixelRatio || 1;
      node.style.width = `${cssW}px`;
      node.style.height = `${cssH}px`;
      node.width = Math.round(cssW * dpr);
      node.height = Math.round(cssH * dpr);
      const ctx = node.getContext('2d');
      ctx.setTransform((cssW * dpr) / W, 0, 0, (cssH * dpr) / H, 0, 0);
      ctx.imageSmoothingEnabled = false;
    };
    size();

    let raf = 0;
    let last = performance.now();
    const loop = (now) => {
      g.tick(now - last);
      last = now;
      drawGame(node.getContext('2d'), g.get());
      raf = window.requestAnimationFrame(loop);
    };
    raf = window.requestAnimationFrame(loop);

    const down = (e) => {
      if (jumpKey(e)) {
        e.preventDefault();
        g.jump();
      }
      if (e.code === 'ArrowDown') {
        e.preventDefault();
        g.setDuck(true);
      }
    };
    const up = (e) => {
      if (e.code === 'ArrowDown') {
        e.preventDefault();
        g.setDuck(false);
      }
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('resize', size);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('resize', size);
    };
  }, []);

  return (
    <div
      className="dino-loader"
      ref={wrap}
      role="application"
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
      aria-label="Dinosaur runner. Space or click to jump while insights load."
      onPointerDown={(e) => {
        e.preventDefault();
        if (game.current) game.current.jump();
      }}
    >
      <canvas className="dino-loader-canvas" ref={canvas} width={W} height={H} />
      <div className="dino-loader-copy">
        <div className="dino-loader-message">{message}</div>
        <div className="dino-loader-hint">Space / click to jump. Down to duck.</div>
      </div>
    </div>
  );
};

DinoLoader.propTypes = {
  message: PropTypes.string,
};

DinoLoader.defaultProps = {
  message: 'Loading Wild Club Insights...',
};

export default DinoLoader;
